<template>
  <AppLayout pageTitle="Projects">
    <!-- Project Detail View -->
    <div v-if="selectedProject">
      <!-- Back Button -->
      <div class="row mb-3">
        <div class="col-12">
          <button class="btn btn-sm fw-semibold" @click="selectedProject = null"
                  style="border-radius: 10px; padding: 8px 18px; background: #f1f5f9; color: #334155;">
            <i class="ph-bold ph-arrow-left me-1"></i> Back to Projects
          </button>
        </div>
      </div>

      <!-- Project Hero -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 overflow-hidden" style="border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.1);">
            <div class="position-relative" style="height: 300px; overflow: hidden;">
              <img v-if="selectedProject.featured_image_url" :src="selectedProject.featured_image_url" 
                   class="w-100 h-100" style="object-fit: cover;" :alt="selectedProject.name">
              <div v-else style="height: 100%; background: linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%); display: flex; align-items: center; justify-content: center;">
                <i class="ph-duotone ph-buildings text-white" style="font-size: 5rem; opacity: 0.3;"></i>
              </div>
              <div class="position-absolute bottom-0 start-0 w-100 p-4" style="background: linear-gradient(transparent, rgba(0,0,0,0.7));">
                <div class="d-flex align-items-end justify-content-between">
                  <div>
                    <span v-if="selectedProject.is_new" class="badge bg-warning text-dark rounded-pill px-3 py-2 f-s-11 fw-bold mb-2">
                      <i class="ph-bold ph-sparkle me-1"></i> New
                    </span>
                    <h2 class="text-white fw-bold mb-1">{{ selectedProject.name }}</h2>
                    <p class="text-white-50 mb-0"><i class="ph-duotone ph-map-pin me-1"></i> {{ selectedProject.location }}</p>
                  </div>
                  <span class="badge rounded-pill px-3 py-2 f-s-12 fw-semibold"
                        :class="selectedProject.status === 'active' || selectedProject.status === 'open' ? 'bg-success' : 'bg-danger'">
                    {{ selectedProject.status }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Project Info -->
      <div class="row mb-4">
        <div class="col-lg-8 mb-4 mb-lg-0">
          <!-- Description -->
          <div class="card border-0 mb-4" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
            <div class="card-body p-4">
              <h5 class="fw-bold mb-3" style="color: #0f172a;">
                <i class="ph-duotone ph-info me-2 text-primary"></i> About This Project
              </h5>
              <p class="text-muted mb-0" style="line-height: 1.8;">{{ selectedProject.description || 'No description available.' }}</p>
              
              <div v-if="selectedProject.hero_tagline" class="mt-3 p-3 rounded-3" style="background: #ecfeff; border-left: 4px solid #0891b2;">
                <p class="mb-0 fw-semibold" style="color: #0891b2;">
                  <i class="ph-duotone ph-quotes me-1"></i> {{ selectedProject.hero_tagline }}
                </p>
              </div>
            </div>
          </div>

          <!-- Gallery -->
          <div v-if="selectedProject.gallery_urls && selectedProject.gallery_urls.length > 0" class="card border-0 mb-4" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
            <div class="card-body p-4">
              <h5 class="fw-bold mb-3" style="color: #0f172a;">
                <i class="ph-duotone ph-images me-2 text-primary"></i> Project Gallery
              </h5>
              <div class="row g-2">
                <div class="col-6 col-md-4" v-for="(img, i) in selectedProject.gallery_urls" :key="i">
                  <div class="rounded-3 overflow-hidden" style="height: 160px; cursor: pointer;" @click="openGallery(i)">
                    <img :src="img" class="w-100 h-100" style="object-fit: cover;" :alt="'Gallery ' + (i+1)">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Project Map -->
          <div class="card border-0 mb-4" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
            <div class="card-body p-4">
              <h5 class="fw-bold mb-3" style="color: #0f172a;">
                <i class="ph-duotone ph-map-trifold me-2 text-primary"></i> Project Map & Plot Layout
              </h5>
              <div ref="projectMap" style="height: 420px; border-radius: 12px; overflow: hidden; border: 2px solid #e2e8f0;"></div>
              <p class="text-muted f-s-12 mt-2 mb-0">
                <i class="ph-duotone ph-cursor-click me-1"></i> Click on a plot marker to view details and express interest
              </p>
            </div>
          </div>

          <!-- Project Plots -->
          <div class="card border-0" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
            <div class="card-header bg-white border-0 pt-4 pb-2 px-4" style="border-radius: 14px 14px 0 0;">
              <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                <h5 class="fw-bold mb-0" style="color: #0f172a;">
                  <i class="ph-duotone ph-map-pin-line me-2 text-warning"></i> Plots in This Project
                </h5>
                <span class="badge bg-light text-dark rounded-pill px-3 py-2 f-s-12">
                  {{ filteredPlots.length }} plots
                </span>
              </div>
              <!-- Search Bar -->
              <div class="mt-3">
                <div class="input-group" style="max-width: 400px;">
                  <span class="input-group-text bg-white border-end-0" style="border-radius: 10px 0 0 10px; border-color: #e2e8f0;">
                    <i class="ph-duotone ph-magnifying-glass text-muted"></i>
                  </span>
                  <input type="text" class="form-control border-start-0" v-model="plotSearch" 
                         placeholder="Search by plot number, type, status..."
                         style="border-radius: 0 10px 10px 0; border-color: #e2e8f0; padding: 10px 14px;">
                </div>
              </div>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive" v-if="filteredPlots.length > 0">
                <table class="table table-hover align-middle mb-0">
                  <thead>
                    <tr style="background: #f8fafc;">
                      <th class="border-0 f-s-13 fw-semibold text-muted px-4 py-3">Plot No.</th>
                      <th class="border-0 f-s-13 fw-semibold text-muted py-3">Size</th>
                      <th class="border-0 f-s-13 fw-semibold text-muted py-3">Type</th>
                      <th class="border-0 f-s-13 fw-semibold text-muted py-3">Price</th>
                      <th class="border-0 f-s-13 fw-semibold text-muted py-3">Status</th>
                      <th class="border-0 f-s-13 fw-semibold text-muted py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="plot in paginatedPlots" :key="plot.id">
                      <td class="px-4 py-3 fw-semibold f-s-13">{{ plot.plot_no }}</td>
                      <td class="py-3 f-s-13 text-muted">{{ plot.size }} sqm</td>
                      <td class="py-3">
                        <span class="badge rounded-pill px-3 py-2 f-s-11" 
                              :style="plot.plot_type === 'commercial' ? 'background:#ecfeff; color:#0891b2;' : 'background:#ecfdf5; color:#10b981;'">
                          {{ plot.plot_type || 'Residential' }}
                        </span>
                      </td>
                      <td class="py-3">
                        <span class="fw-bold f-s-14" :class="store.balancesHidden ? 'balance-blurred' : 'balance-visible'" style="color: #f59e0b;">
                          KES {{ Number(plot.price).toLocaleString() }}
                        </span>
                      </td>
                      <td class="py-3">
                        <span class="badge rounded-pill px-3 py-2 f-s-11"
                              :class="plot.status === 'available' ? 'bg-light text-success' : plot.status === 'sold' ? 'bg-light text-danger' : 'bg-light text-warning'">
                          <i :class="plot.status === 'available' ? 'ph-bold ph-check-circle' : plot.status === 'sold' ? 'ph-bold ph-x-circle' : 'ph-bold ph-clock'" class="me-1"></i>
                          {{ plot.status }}
                        </span>
                      </td>
                      <td class="py-3">
                        <button v-if="plot.status === 'available'" class="btn btn-sm fw-semibold"
                                @click="showInterestModal(plot)"
                                style="background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; border-radius: 8px; padding: 5px 14px; font-size: 12px;">
                          <i class="ph-duotone ph-hand-waving me-1"></i> Express Interest
                        </button>
                        <span v-else class="text-muted f-s-12">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <div v-if="plotTotalPages > 1" class="d-flex justify-content-between align-items-center px-4 py-3 border-top" style="background: #f8fafc;">
                <span class="text-muted f-s-12">
                  Showing {{ (plotCurrentPage - 1) * plotsPerPage + 1 }}–{{ Math.min(plotCurrentPage * plotsPerPage, filteredPlots.length) }} of {{ filteredPlots.length }}
                </span>
                <div class="d-flex align-items-center gap-1">
                  <button class="btn btn-sm border-0 rounded-2 fw-medium" :disabled="plotCurrentPage === 1" @click="plotCurrentPage > 1 && plotCurrentPage--"
                          style="color: #0891b2; background: transparent; min-width: 32px; height: 32px; padding: 0;">
                    <i class="ph-bold ph-caret-left"></i>
                  </button>
                  <template v-for="page in plotDisplayPages" :key="'p'+page">
                    <button v-if="page === '...'" class="btn btn-sm border-0 rounded-2 f-s-12 text-muted" disabled
                            style="background: transparent; min-width: 32px; height: 32px; padding: 0;">...</button>
                    <button v-else class="btn btn-sm border-0 rounded-2 fw-semibold f-s-12" @click="plotCurrentPage = page"
                            :style="page === plotCurrentPage ? 'background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; min-width: 32px; height: 32px; padding: 0;' : 'color: #64748b; background: transparent; min-width: 32px; height: 32px; padding: 0;'">
                      {{ page }}
                    </button>
                  </template>
                  <button class="btn btn-sm border-0 rounded-2 fw-medium" :disabled="plotCurrentPage === plotTotalPages" @click="plotCurrentPage < plotTotalPages && plotCurrentPage++"
                          style="color: #0891b2; background: transparent; min-width: 32px; height: 32px; padding: 0;">
                    <i class="ph-bold ph-caret-right"></i>
                  </button>
                </div>
              </div>

              <div v-if="filteredPlots.length === 0 && plotSearch" class="text-center py-5">
                <i class="ph-duotone ph-magnifying-glass f-s-30 text-muted"></i>
                <p class="text-muted mt-2 mb-0">No plots match "{{ plotSearch }}"</p>
              </div>
              <div v-else-if="filteredPlots.length === 0" class="text-center py-5">
                <i class="ph-duotone ph-map-pin-line f-s-30 text-muted"></i>
                <p class="text-muted mt-2 mb-0">No plots listed for this project yet</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="col-lg-4">
          <!-- Pricing -->
          <div v-if="selectedProject.pricing && Object.keys(selectedProject.pricing).length" class="card border-0 mb-4" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
            <div class="card-body p-4">
              <h6 class="fw-bold mb-3" style="color: #0f172a;">
                <i class="ph-duotone ph-tag me-2 text-success"></i> Pricing
              </h6>
              <div v-for="(val, key) in selectedProject.pricing" :key="key" class="d-flex justify-content-between py-2 border-bottom">
                <span class="fw-medium f-s-13 text-muted">{{ key }}</span>
                <span class="fw-bold f-s-13" :class="store.balancesHidden ? 'balance-blurred' : 'balance-visible'" style="color: #0f172a;">{{ val }}</span>
              </div>
            </div>
          </div>

          <!-- Amenities -->
          <div v-if="selectedProject.amenities && Object.keys(selectedProject.amenities).length" class="card border-0 mb-4" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
            <div class="card-body p-4">
              <h6 class="fw-bold mb-3" style="color: #0f172a;">
                <i class="ph-duotone ph-star me-2 text-warning"></i> Amenities
              </h6>
              <div v-for="(val, key) in selectedProject.amenities" :key="key" class="d-flex align-items-center py-2">
                <i class="ph-bold ph-check-circle text-success me-2 f-s-14"></i>
                <span class="f-s-13 text-dark">{{ key }}: {{ val }}</span>
              </div>
            </div>
          </div>

          <!-- Payment Options -->
          <div v-if="selectedProject.payment_options && Object.keys(selectedProject.payment_options).length" class="card border-0 mb-4" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
            <div class="card-body p-4">
              <h6 class="fw-bold mb-3" style="color: #0f172a;">
                <i class="ph-duotone ph-credit-card me-2 text-primary"></i> Payment Options
              </h6>
              <div v-for="(val, key) in selectedProject.payment_options" :key="key" class="d-flex justify-content-between py-2 border-bottom">
                <span class="fw-medium f-s-13 text-muted">{{ key }}</span>
                <span class="fw-semibold f-s-13 text-dark">{{ val }}</span>
              </div>
            </div>
          </div>

          <!-- CTA -->
          <div v-if="selectedProject.cta_text" class="card border-0" style="border-radius: 14px; background: linear-gradient(135deg, #0891b2, #06b6d4);">
            <div class="card-body p-4 text-center">
              <h6 class="text-white fw-bold mb-2">Interested in this project?</h6>
              <p class="text-white-50 f-s-13 mb-3">Get in touch with us to learn more about available plots</p>
              <a :href="selectedProject.cta_link || '#'" class="btn btn-light fw-semibold" style="border-radius: 10px; padding: 10px 24px;">
                {{ selectedProject.cta_text }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Projects List View -->
    <div v-else>
      <!-- Header -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 overflow-hidden" style="border-radius: 16px; background: linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%);">
            <div class="card-body py-4 px-4">
              <div class="d-flex align-items-center">
                <div class="rounded-3 d-flex align-items-center justify-content-center me-3" style="width: 52px; height: 52px; background: rgba(255,255,255,0.25);">
                  <i class="ph-duotone ph-buildings text-white f-s-24"></i>
                </div>
                <div>
                  <h4 class="text-white fw-bold mb-1">Our Projects</h4>
                  <p class="text-white-50 mb-0 f-s-14">Discover Sheria Housing developments and explore available plots</p>
                </div>
                <div class="ms-auto d-none d-sm-block">
                  <span class="badge bg-white bg-opacity-25 text-white px-3 py-2 rounded-pill fw-semibold">
                    {{ filteredProjectList.length }} of {{ projects.length }} Projects
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Search Bar -->
      <div class="row mb-3" v-if="!isLoading && projects.length > 0">
        <div class="col-12">
          <div class="card border-0" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
            <div class="card-body py-3 px-4">
              <div class="d-flex align-items-center" style="max-width: 480px;">
                <div class="input-group">
                  <span class="input-group-text bg-white border-end-0" style="border-radius: 10px 0 0 10px; border-color: #e2e8f0;">
                    <i class="ph-duotone ph-magnifying-glass text-muted"></i>
                  </span>
                  <input type="text" class="form-control border-start-0" v-model="projectSearch"
                         placeholder="Search projects by name, location..."
                         style="border-radius: 0 10px 10px 0; border-color: #e2e8f0; padding: 10px 14px;">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Projects Grid -->
      <div class="row" v-if="!isLoading">
        <div class="col-lg-4 col-md-6 mb-4" v-for="project in filteredProjectList" :key="project.id">
          <div class="card border-0 h-100 overflow-hidden" 
               style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06); cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;"
               @click="viewProject(project)"
               @mouseover="$event.currentTarget.style.transform='translateY(-4px)'; $event.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.12)'"
               @mouseout="$event.currentTarget.style.transform='translateY(0)'; $event.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.06)'">
            <div class="position-relative" style="height: 200px; overflow: hidden;">
              <img v-if="project.featured_image_url" :src="project.featured_image_url" 
                   class="w-100 h-100" style="object-fit: cover;" :alt="project.name">
              <div v-else style="height: 100%; background: linear-gradient(135deg, #0891b2, #06b6d4); display: flex; align-items: center; justify-content: center;">
                <i class="ph-duotone ph-buildings text-white" style="font-size: 3rem; opacity: 0.5;"></i>
              </div>
              <div class="position-absolute top-0 end-0 p-2 d-flex gap-1">
                <span v-if="project.is_new" class="badge bg-warning text-dark rounded-pill px-2 py-1 f-s-10 fw-bold">NEW</span>
                <span v-if="project.is_featured" class="badge bg-primary text-white rounded-pill px-2 py-1 f-s-10 fw-bold">FEATURED</span>
              </div>
              <div class="position-absolute bottom-0 start-0 w-100 p-3" style="background: linear-gradient(transparent, rgba(0,0,0,0.6));">
                <span class="badge rounded-pill px-2 py-1 f-s-10 fw-semibold"
                      :class="project.status === 'active' || project.status === 'open' ? 'bg-success' : 'bg-danger'">
                  {{ project.status }}
                </span>
              </div>
            </div>
            <div class="card-body">
              <div class="d-flex align-items-center mb-2">
                <span class="text-muted f-s-12">
                  <i class="ph-duotone ph-map-pin me-1"></i> {{ project.location }}
                </span>
              </div>
              <h5 class="fw-bold mb-2" style="color: #0f172a;">{{ project.name }}</h5>
              <p class="text-muted f-s-13 mb-3" style="line-height: 1.6; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">
                {{ project.description || 'Explore this project for details.' }}
              </p>
              <div class="d-flex align-items-center justify-content-between">
                <span class="text-muted f-s-12"><i class="ph-duotone ph-map-pin-line me-1"></i> {{ project.plots ? project.plots.length : 0 }} plots</span>
                <span class="fw-semibold f-s-13" style="color: #0891b2;">
                  View Details <i class="ph-bold ph-arrow-right ms-1"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-if="filteredProjectList.length === 0" class="col-12 text-center py-5">
          <i class="ph-duotone ph-buildings f-s-30 text-muted"></i>
          <p class="text-muted mt-2">{{ projectSearch ? 'No projects match "' + projectSearch + '"' : 'No projects available at this time' }}</p>
        </div>
      </div>

      <!-- Loading -->
      <div class="row" v-else>
        <div class="col-lg-4 col-md-6 mb-4" v-for="n in 6" :key="n">
          <div class="card border-0" style="border-radius: 14px;">
            <div class="placeholder-glow">
              <span class="placeholder col-12" style="height: 200px; border-radius: 14px 14px 0 0;"></span>
            </div>
            <div class="card-body placeholder-glow">
              <span class="placeholder col-4 rounded mb-2"></span>
              <span class="placeholder col-8 rounded mb-2" style="height: 20px;"></span>
              <span class="placeholder col-12 rounded"></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Expression of Interest Modal -->
    <div v-if="showEoiModal" class="modal d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);" @click.self="showEoiModal = false">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0" style="border-radius: 16px; overflow: hidden;">
          <div class="modal-header border-0 pb-0 px-4 pt-4">
            <h5 class="fw-bold" style="color: #0f172a;">
              <i class="ph-duotone ph-hand-waving me-2 text-primary"></i> Express Interest
            </h5>
            <button type="button" class="btn-close" @click="showEoiModal = false"></button>
          </div>
          <div class="modal-body px-4 pb-4">
            <div class="p-3 rounded-3 mb-3" style="background: #ecfeff;">
              <p class="mb-0 f-s-13" style="color: #0891b2;">
                <i class="ph-duotone ph-map-pin-line me-1"></i>
                <strong>Plot {{ eoiPlot?.plot_no }}</strong> — {{ eoiPlot?.size }} sqm • KES {{ eoiPlot?.price ? Number(eoiPlot.price).toLocaleString() : '—' }}
              </p>
            </div>

            <div v-if="eoiSuccess" class="alert border-0 mb-3" style="background: #ecfdf5; color: #10b981; border-radius: 12px;">
              <i class="ph-bold ph-check-circle me-2"></i> {{ eoiSuccess }}
            </div>
            <div v-if="eoiError" class="alert border-0 mb-3" style="background: #fef2f2; color: #ef4444; border-radius: 12px;">
              <i class="ph-bold ph-warning-circle me-2"></i> {{ eoiError }}
            </div>

            <p class="text-muted f-s-13 mb-3">
              By submitting, your contact details will be shared with Sheria Housing for follow-up regarding this plot.
            </p>

            <button class="btn w-100 fw-semibold" @click="submitInterest" :disabled="eoiLoading"
                    style="background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; border-radius: 10px; padding: 12px;">
              <span v-if="eoiLoading" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="ph-duotone ph-paper-plane-tilt me-2"></i>
              {{ eoiLoading ? 'Submitting...' : 'Confirm Interest' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import api from '@/services/axios.js';
import { useUserStore } from '@/store';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export default {
  name: 'ProjectsPage',
  components: { AppLayout },
  data() {
    return {
      projects: [],
      selectedProject: null,
      projectPlots: [],
      isLoading: true,
      showEoiModal: false,
      eoiPlot: null,
      eoiLoading: false,
      eoiSuccess: '',
      eoiError: '',
      plotSearch: '',
      projectSearch: '',
      plotCurrentPage: 1,
      plotsPerPage: 10,
      map: null,
    };
  },
  computed: {
    store() {
      return useUserStore();
    },
    filteredProjectList() {
      if (!this.projectSearch.trim()) return this.projects;
      const q = this.projectSearch.toLowerCase().trim();
      return this.projects.filter(p =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.location && p.location.toLowerCase().includes(q)) ||
        (p.description && p.description.toLowerCase().includes(q)) ||
        (p.status && p.status.toLowerCase().includes(q))
      );
    },
    filteredPlots() {
      if (!this.plotSearch.trim()) return this.projectPlots;
      const q = this.plotSearch.toLowerCase().trim();
      return this.projectPlots.filter(p =>
        (p.plot_no && p.plot_no.toLowerCase().includes(q)) ||
        (p.plot_type && p.plot_type.toLowerCase().includes(q)) ||
        (p.status && p.status.toLowerCase().includes(q)) ||
        (p.size && String(p.size).includes(q))
      );
    },
    plotTotalPages() {
      return Math.ceil(this.filteredPlots.length / this.plotsPerPage);
    },
    plotDisplayPages() {
      const total = this.plotTotalPages;
      const current = this.plotCurrentPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [];
      pages.push(1);
      if (current > 3) pages.push('...');
      for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
      if (current < total - 2) pages.push('...');
      pages.push(total);
      return pages;
    },
    paginatedPlots() {
      const start = (this.plotCurrentPage - 1) * this.plotsPerPage;
      return this.filteredPlots.slice(start, start + this.plotsPerPage);
    },
  },
  watch: {
    plotSearch() {
      this.plotCurrentPage = 1;
    },
  },
  mounted() {
    this.fetchProjects();
  },
  beforeUnmount() {
    this.destroyMap();
  },
  methods: {
    async fetchProjects() {
      try {
        this.isLoading = true;
        const response = await api.get('/api/project-list?portal=true');
        this.projects = response.data || [];
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async viewProject(project) {
      try {
        const response = await api.get(`/api/projects/${project.slug || project.id}`);
        this.selectedProject = response.data.data || response.data;
        this.projectPlots = this.selectedProject.plots || [];
      } catch (error) {
        console.error('Failed to load project:', error);
        this.selectedProject = project;
        this.projectPlots = project.plots || [];
      }
      this.plotSearch = '';
      this.plotCurrentPage = 1;
      this.$nextTick(() => this.initMap());
    },
    destroyMap() {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
    },
    initMap() {
      this.destroyMap();
      const container = this.$refs.projectMap;
      if (!container) return;

      // Surveyor-style map: white canvas, no street tiles
      this.map = L.map(container, {
        zoomControl: true,
        scrollWheelZoom: true,
        attributionControl: false,
      });

      // Plain white background (no OSM tiles) — mimics a survey plan
      container.style.background = '#ffffff';

      const bounds = L.latLngBounds();
      let hasBounds = false;

      // Draw project boundary polygon — thick, prominent survey-style border
      const boundary = this.selectedProject?.boundary;
      if (boundary) {
        try {
          let geojson = boundary;
          if (typeof geojson === 'string') geojson = JSON.parse(geojson);
          const layer = L.geoJSON(geojson, {
            style: {
              color: '#1e293b',
              weight: 3,
              fillColor: '#f8fafc',
              fillOpacity: 0.4,
              dashArray: null,
            },
          }).addTo(this.map);
          bounds.extend(layer.getBounds());
          hasBounds = true;
        } catch (e) { console.warn('Invalid boundary GeoJSON:', e); }
      }

      // Add plot markers/polygons — survey plan style
      this.projectPlots.forEach(plot => {
        const coords = plot.coordinates;
        if (!coords) return;

        try {
          let parsed = typeof coords === 'string' ? JSON.parse(coords) : coords;
          const statusColor = plot.status === 'available' ? '#10b981' : plot.status === 'sold' ? '#ef4444' : '#f59e0b';
          const fillColor = plot.status === 'available' ? '#ecfdf5' : plot.status === 'sold' ? '#fef2f2' : '#fffbeb';

          // If coords is a [lat, lng] point
          if (Array.isArray(parsed) && parsed.length === 2 && typeof parsed[0] === 'number') {
            const latlng = [parsed[0], parsed[1]];
            const icon = L.divIcon({
              className: 'survey-plot-label',
              html: `<div style="background:${fillColor}; color:${statusColor}; border:2px solid ${statusColor}; border-radius:4px; padding:3px 8px; font-size:11px; font-weight:700; white-space:nowrap; text-align:center; font-family:monospace;">${plot.plot_no || 'Plot'}</div>`,
              iconSize: null,
              iconAnchor: [20, 16],
              popupAnchor: [0, -12],
            });
            const marker = L.marker(latlng, { icon }).addTo(this.map);
            marker.bindPopup(this.plotPopup(plot));
            bounds.extend(latlng);
            hasBounds = true;
          }
          // If coords is a GeoJSON geometry (Polygon, MultiPolygon, etc.)
          else if (parsed.type) {
            const layer = L.geoJSON(parsed, {
              style: {
                color: statusColor,
                weight: 2,
                fillColor: fillColor,
                fillOpacity: 0.5,
              },
              onEachFeature: (f, l) => {
                l.bindPopup(this.plotPopup(plot));
                l.bindTooltip(plot.plot_no || 'Plot', {
                  permanent: true,
                  direction: 'center',
                  className: 'survey-label-tooltip',
                });
              },
            }).addTo(this.map);
            bounds.extend(layer.getBounds());
            hasBounds = true;
          }
          // If coords is an array of [lat, lng] pairs (polygon)
          else if (Array.isArray(parsed) && Array.isArray(parsed[0])) {
            const latLngs = parsed.map(c => [c[0], c[1]]);
            const poly = L.polygon(latLngs, {
              color: statusColor,
              weight: 2,
              fillColor: fillColor,
              fillOpacity: 0.5,
            }).addTo(this.map);
            poly.bindPopup(this.plotPopup(plot));
            poly.bindTooltip(plot.plot_no || 'Plot', {
              permanent: true,
              direction: 'center',
              className: 'survey-label-tooltip',
            });
            bounds.extend(poly.getBounds());
            hasBounds = true;
          }
        } catch (e) { console.warn('Invalid plot coordinates:', plot.plot_no, e); }
      });

      // Add a legend
      const legend = L.control({ position: 'bottomright' });
      legend.onAdd = function() {
        const div = L.DomUtil.create('div', 'survey-legend');
        div.innerHTML = `
          <div style="background:white; border:2px solid #e2e8f0; border-radius:8px; padding:10px 14px; font-family:system-ui; font-size:11px; box-shadow:0 2px 8px rgba(0,0,0,0.1);">
            <div style="font-weight:700; margin-bottom:6px; color:#0f172a; font-size:12px;">PLOT STATUS</div>
            <div style="display:flex; align-items:center; gap:6px; margin-bottom:4px;"><span style="width:12px;height:12px;background:#ecfdf5;border:2px solid #10b981;border-radius:2px;display:inline-block;"></span> Available</div>
            <div style="display:flex; align-items:center; gap:6px; margin-bottom:4px;"><span style="width:12px;height:12px;background:#fffbeb;border:2px solid #f59e0b;border-radius:2px;display:inline-block;"></span> Reserved</div>
            <div style="display:flex; align-items:center; gap:6px;"><span style="width:12px;height:12px;background:#fef2f2;border:2px solid #ef4444;border-radius:2px;display:inline-block;"></span> Sold</div>
          </div>
        `;
        return div;
      };
      legend.addTo(this.map);

      // Add compass rose (North arrow)
      const compass = L.control({ position: 'topright' });
      compass.onAdd = function() {
        const div = L.DomUtil.create('div', 'survey-compass');
        div.innerHTML = `
          <div style="background:white; border:2px solid #e2e8f0; border-radius:8px; padding:8px 12px; text-align:center; box-shadow:0 2px 8px rgba(0,0,0,0.1);">
            <div style="font-weight:900; font-size:14px; color:#0f172a;">N</div>
            <div style="font-size:18px; color:#0891b2;">↑</div>
          </div>
        `;
        return div;
      };
      compass.addTo(this.map);

      if (hasBounds && bounds.isValid()) {
        this.map.fitBounds(bounds, { padding: [30, 30] });
      } else {
        // Default to Kenya center
        this.map.setView([-1.286389, 36.817223], 10);
      }
    },
    plotPopup(plot) {
      const statusColor = plot.status === 'available' ? '#10b981' : plot.status === 'sold' ? '#ef4444' : '#f59e0b';
      return `
        <div style="min-width:180px; font-family: inherit;">
          <h6 style="font-weight:700; margin:0 0 8px; color:#0f172a;">Plot ${plot.plot_no || '—'}</h6>
          <div style="font-size:13px; color:#64748b; margin-bottom:4px;">
            <strong>Size:</strong> ${plot.size || '—'} sqm
          </div>
          <div style="font-size:13px; color:#64748b; margin-bottom:4px;">
            <strong>Type:</strong> ${plot.plot_type || 'Residential'}
          </div>
          <div style="font-size:13px; color:#64748b; margin-bottom:4px;">
            <strong>Price:</strong> <span style="color:#f59e0b; font-weight:700;">KES ${plot.price ? Number(plot.price).toLocaleString() : '—'}</span>
          </div>
          <div style="font-size:12px; margin-bottom:8px;">
            <span style="display:inline-block; padding:2px 10px; border-radius:20px; background:${statusColor}15; color:${statusColor}; font-weight:600;">${plot.status || '—'}</span>
          </div>
          ${plot.status === 'available' ? '<button onclick="document.dispatchEvent(new CustomEvent(\'plot-interest\', {detail:' + plot.id + '}))" style="width:100%; padding:6px 12px; border:none; border-radius:8px; background:linear-gradient(135deg, #0891b2, #06b6d4); color:white; font-weight:600; font-size:12px; cursor:pointer;">Express Interest</button>' : ''}
        </div>
      `;
    },
    showInterestModal(plot) {
      this.eoiPlot = plot;
      this.eoiSuccess = '';
      this.eoiError = '';
      this.showEoiModal = true;
    },
    handlePlotInterest(e) {
      const plotId = e.detail;
      const plot = this.projectPlots.find(p => p.id === plotId);
      if (plot) this.showInterestModal(plot);
    },
    async submitInterest() {
      try {
        this.eoiLoading = true;
        this.eoiError = '';
        await api.post('/api/plot-interest', { plot_id: this.eoiPlot.id });
        this.eoiSuccess = 'Your interest has been submitted successfully! Our team will contact you soon.';
        setTimeout(() => { this.showEoiModal = false; }, 3000);
      } catch (error) {
        this.eoiError = error.response?.data?.message || error.response?.data?.error || 'Failed to submit interest. Please try again.';
      } finally {
        this.eoiLoading = false;
      }
    },
    openGallery(index) {
      if (this.selectedProject?.gallery_urls?.[index]) {
        window.open(this.selectedProject.gallery_urls[index], '_blank');
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
  created() {
    document.addEventListener('plot-interest', this.handlePlotInterest);
  },
  unmounted() {
    document.removeEventListener('plot-interest', this.handlePlotInterest);
  },
};
</script>

<style scoped>
/* Survey plan / cadastral map label styling */
.survey-label-tooltip {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #0f172a !important;
  border: 1.5px solid #94a3b8 !important;
  border-radius: 3px !important;
  padding: 2px 6px !important;
  font-size: 10px !important;
  font-weight: 700 !important;
  font-family: 'Courier New', monospace !important;
  box-shadow: none !important;
  letter-spacing: 0.5px !important;
}
.survey-label-tooltip::before {
  display: none !important;
}

/* Map container survey paper styling */
:deep(.leaflet-container) {
  background: #ffffff !important;
  font-family: 'Courier New', monospace !important;
}
:deep(.leaflet-control-zoom) {
  border: 2px solid #e2e8f0 !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
}
:deep(.leaflet-control-zoom a) {
  color: #0f172a !important;
  background: white !important;
}
</style>